/* ============================================================
   Админка · Дашборд: карточки метрик + последние новости и заявки.
   ============================================================ */
import { isPublished, useFedContent } from '../content';
import { Ic } from '../icons';
import { tournamentPhase } from '../data';
import { AdmCard, StPill } from './ui';

const NEWS_VISUALS = {
  'Турниры': { icon: 'trophy', color: '#ef4444', bg: 'rgba(239,68,68,.14)', border: 'rgba(239,68,68,.32)' },
  'Анонсы': { icon: 'bell', color: '#f59e0b', bg: 'rgba(245,158,11,.14)', border: 'rgba(245,158,11,.3)' },
  'Федерация': { icon: 'hand', color: '#22c55e', bg: 'rgba(34,197,94,.14)', border: 'rgba(34,197,94,.3)' },
  'Обучение': { icon: 'cap', color: '#9b6cf6', bg: 'rgba(139,92,246,.15)', border: 'rgba(139,92,246,.32)' },
  'Медиа': { icon: 'video', color: '#38bdf8', bg: 'rgba(56,189,248,.14)', border: 'rgba(56,189,248,.3)' },
};

const newsVisual = (category) => NEWS_VISUALS[category] || { icon: 'news', color: '#9b6cf6', bg: 'rgba(139,92,246,.15)', border: 'rgba(139,92,246,.32)' };

function StatCard({ icon, tint, value, label, delta }) {
  return (
    <AdmCard className="fed-adm-stat" style={{ padding: 22 }}>
      <div className="flex items-start justify-between">
        <div className="fed-adm-stat-icon rounded-[10px] flex items-center justify-center" style={{ width: 40, height: 40, background: tint.bg, color: tint.fg, border: `1px solid ${tint.bd}` }}>
          <Ic name={icon} size={20} />
        </div>
        {delta ? <span className="fed-adm-stat-delta font-mono text-fg-faint" style={{ fontSize: 11 }}>{delta}</span> : null}
      </div>
      <div className="fed-adm-stat-value font-display font-bold text-fg" style={{ fontSize: 38, marginTop: 14, lineHeight: 1 }}>{value}</div>
      <div className="fed-adm-stat-label text-fg-muted" style={{ fontSize: 13, marginTop: 7 }}>{label}</div>
    </AdmCard>
  );
}

export default function AdmDashboard({ regs, goSection }) {
  const news = useFedContent((s) => s.news);
  const tournaments = useFedContent((s) => s.tournaments);
  const sponsorRequests = useFedContent((s) => s.sponsorRequests);

  const newRegs = regs.filter((r) => r.status === 'new').length;
  const newSp = sponsorRequests.filter((r) => r.status === 'new').length;

  // Все дельты считаются от реальных данных: раньше у турниров стояла
  // константа «+2 за сезон», не связанная ни с чем.
  const liveTours = tournaments.filter((t) => tournamentPhase(t) === 'live').length;
  const registrationTours = tournaments.filter((t) => tournamentPhase(t) === 'registration').length;
  const soonTours = tournaments.filter((t) => tournamentPhase(t) === 'soon').length;
  const publishedNews = news.filter(isPublished).length;
  const draftNews = news.length - publishedNews;

  const stats = [
    { icon: 'trophy', tint: { bg: 'rgba(225,18,23,.1)', fg: 'var(--fed-accent-bright)', bd: 'var(--fed-line-accent)' }, value: tournaments.length, label: 'Турниров всего', delta: registrationTours ? `${registrationTours} с открытой регистрацией` : liveTours ? `${liveTours} идёт` : soonTours ? `${soonTours} в анонсе` : '' },
    { icon: 'news', tint: { bg: 'rgba(230,183,60,.1)', fg: 'var(--fed-gold)', bd: 'rgba(230,183,60,.35)' }, value: publishedNews, label: 'Опубликовано новостей', delta: draftNews ? `${draftNews} в черновиках` : '' },
    { icon: 'inbox', tint: { bg: 'rgba(59,165,217,.12)', fg: 'var(--fed-info)', bd: 'rgba(59,165,217,.35)' }, value: regs.length, label: 'Заявок на участие', delta: newRegs ? `${newRegs} новых` : '' },
    { icon: 'money', tint: { bg: 'rgba(230,183,60,.1)', fg: 'var(--fed-gold)', bd: 'rgba(230,183,60,.35)' }, value: sponsorRequests.length, label: 'Спонсорских заявок', delta: newSp ? `${newSp} новых` : '' },
  ];

  return (
    <div className="fed-admin-view flex flex-col gap-6">
      <div className="fed-adm-statgrid grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))' }}>
        {stats.map((s) => <StatCard key={s.label} {...s} />)}
      </div>

      <div className="fed-adm-dashboard-lists grid gap-4" style={{ gridTemplateColumns: '1.3fr 1fr' }}>
        {/* последние новости */}
        <AdmCard className="fed-adm-news-panel" style={{ padding: 20 }}>
          <div className="fed-adm-news-head flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <span className="fed-adm-news-headicon"><Ic name="news" size={20} /></span>
              <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 18 }}>Последние новости</div>
            </div>
            <button onClick={() => goSection('news')} className="fed-adm-news-all">Все новости <span>→</span></button>
          </div>
          <div className="fed-adm-news-stack">
            {news.slice(0, 4).map((n) => {
              const visual = newsVisual(n.cat_ru);
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => goSection('newsEdit', { editId: n.id })}
                  className="fed-adm-news-row"
                  style={{ '--news-accent': visual.color, '--news-bg': visual.bg, '--news-border': visual.border }}
                >
                  <span className="fed-adm-news-icon"><Ic name={visual.icon} size={27} sw={1.8} /></span>
                  <span className="fed-adm-news-copy">
                    <span className="fed-adm-news-cat">{n.cat_ru}</span>
                    <span className="fed-adm-news-title">{n.title_ru}</span>
                  </span>
                  <span className="fed-adm-news-date"><i />{n.date}</span>
                </button>
              );
            })}
            {!news.length && <div className="fed-adm-news-empty">Новостей пока нет</div>}
          </div>
        </AdmCard>

        {/* новые заявки */}
        <AdmCard style={{ padding: 20 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
            <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 16 }}>Новые заявки</div>
            <button onClick={() => goSection('regs')} className="bg-transparent border-none cursor-pointer text-accent-bright font-semibold" style={{ fontSize: 13 }}>Все →</button>
          </div>
          <div className="flex flex-col">
            {regs.slice(0, 4).map((r) => (
              <div key={r.id} className="flex items-center gap-3 border-t border-line first:border-t-0" style={{ padding: '11px 0' }}>
                <span className="flex items-center justify-center rounded-lg font-mono font-bold text-fg bg-surface-3 flex-shrink-0" style={{ width: 32, height: 32, fontSize: 13 }}>
                  {(r.nick || '?')[0].toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-fg truncate" style={{ fontSize: 13.5 }}>{r.nick}</div>
                  <div className="font-mono text-fg-faint truncate" style={{ fontSize: 10.5, marginTop: 2 }}>{r.tournament}</div>
                </div>
                <StPill st={r.status} />
              </div>
            ))}
            {!regs.length && <div className="text-fg-faint text-center" style={{ padding: '20px 0', fontSize: 13 }}>Заявок пока нет</div>}
          </div>
        </AdmCard>
      </div>
    </div>
  );
}
